import type { ReactNode } from "react";
import { COLORS } from "../lib/brand";
import { Platform, Pressable, Text, View, type ViewStyle } from "react-native";

const FOREST = COLORS.FOREST.hex;
const FOREST_DEEP = COLORS.FOREST_DEEP.hex;
const AMBER = COLORS.AMBER.hex;
const LINEN = COLORS.LINEN.hex;
const TEXT_DIM = "rgba(237,232,223,0.62)";
const TEXT_FAINT = "rgba(237,232,223,0.40)";
const BORDER_SOFT = "rgba(237,232,223,0.08)";

/**
 * Canonical dark-themed empty state. Use across every list / dashboard
 * surface when the fetch returns zero rows.
 *
 * Props mirror the BP-defined shape: small AMBER eyebrow → Cormorant
 * headline → muted body → optional AMBER CTA. Centered card, max-width
 * 500px, FOREST_DEEP background.
 *
 * Light-themed campus surfaces should keep using `components/ui/EmptyState`
 * (the legacy sand/light variant). Don't try to make this one polymorphic
 * — the visual contract is different enough that a `variant` prop just
 * obscures intent.
 */
export function EmptyState({
  eyebrow,
  title,
  body,
  ctaLabel,
  onCtaPress,
}: {
  /** Short uppercase tag above the headline, e.g. "NO INQUIRIES". Optional. */
  eyebrow?: string;
  /** One-line headline in serif. Required. */
  title: string;
  /** 1-2 sentences explaining what shows up here and how. Optional. */
  body?: string;
  /** Optional AMBER call-to-action when there's something the user can do. */
  ctaLabel?: string;
  onCtaPress?: () => void;
}) {
  return (
    <View
      style={{
        alignSelf: "center",
        width: "100%",
        maxWidth: 500,
        marginTop: 24,
        paddingVertical: 32,
        paddingHorizontal: 24,
        borderRadius: 8,
        backgroundColor: FOREST_DEEP,
        borderWidth: 1,
        borderColor: BORDER_SOFT,
        alignItems: "center",
      }}
    >
      {eyebrow ? (
        <Text
          style={{
            fontFamily: "DMSans_700Bold",
            fontSize: 10.5,
            letterSpacing: 2,
            textTransform: "uppercase",
            color: AMBER,
            marginBottom: 10,
          }}
        >
          {eyebrow}
        </Text>
      ) : null}
      <Text
        style={{
          fontFamily: "CormorantGaramond_400Regular",
          fontSize: 24,
          lineHeight: 30,
          color: LINEN,
          textAlign: "center",
          letterSpacing: -0.3,
        }}
      >
        {title}
      </Text>
      {body ? (
        <Text
          style={{
            marginTop: 10,
            fontFamily: "DMSans_400Regular",
            fontSize: 13.5,
            lineHeight: 21,
            color: TEXT_DIM,
            textAlign: "center",
            maxWidth: 420,
          }}
        >
          {body}
        </Text>
      ) : null}
      {ctaLabel && onCtaPress ? (
        <Pressable
          onPress={onCtaPress}
          accessibilityRole="button"
          style={({ hovered, pressed }) => [
            {
              marginTop: 18,
              paddingHorizontal: 18,
              paddingVertical: 11,
              borderRadius: 4,
              backgroundColor: AMBER,
            } as ViewStyle,
            Platform.OS === "web" ? ({ cursor: "pointer" } as const) : null,
            Platform.OS === "web" && hovered
              ? ({ backgroundColor: "#C29347" } as ViewStyle)
              : null,
            pressed ? ({ opacity: 0.92 } as ViewStyle) : null,
          ]}
        >
          <Text
            style={{
              fontFamily: "DMSans_700Bold",
              fontSize: 11.5,
              letterSpacing: 1.4,
              textTransform: "uppercase",
              color: FOREST,
            }}
          >
            {ctaLabel}
          </Text>
        </Pressable>
      ) : null}
    </View>
  );
}

// Kept alive for the dim text helper — TEXT_FAINT is referenced by some
// downstream call sites that import constants from this file.
void TEXT_FAINT;
